import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  computed,
  NgZone,
  inject,
  signal
} from '@angular/core';

import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { ImpersonationService } from '../services/impersonation.service';

/**
 * Persistent banner shown at the top of the app whenever an admin is
 * impersonating another user. Renders nothing when not impersonating.
 *
 * Monté dans le shell global (app.component.html, `.global-banner-stack`) :
 * l'utilisateur impersonné navigue hors de /admin et doit toujours la voir.
 * The session-storage-backed `ImpersonationService.context()` signal
 * survives navigation, refreshes (within the 15 min TTL).
 */
@Component({
  selector: 'app-impersonation-banner',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatIconModule, TranslatePipe],
  template: `
    @if (impersonation.context(); as ctx) {
      <div class="impersonation-banner" role="status" aria-live="polite">
        <mat-icon>visibility</mat-icon>
        <span class="banner-text">
          {{ 'admin.impersonationBanner.connectedAs' | translate }}
          <strong>{{ ctx.targetEmail || ctx.targetUid }}</strong>
          ({{
            'admin.impersonationBanner.adminLabel'
              | translate: { admin: ctx.adminEmail }
          }}) ·
          {{
            'admin.impersonationBanner.expiresIn'
              | translate: { time: remainingLabel() }
          }}
        </span>
        <button mat-stroked-button color="warn" (click)="exit()">
          <mat-icon>logout</mat-icon>
          {{ 'admin.impersonationBanner.exit' | translate }}
        </button>
      </div>
    }
  `,
  styles: [
    `
      .impersonation-banner {
        /* Le conteneur .global-banner-stack est déjà sticky : pas de second sticky. */
        position: relative;
        background: #fef3c7;
        border-bottom: 2px solid #f59e0b;
        color: #92400e;
        padding: 8px 16px;
        display: flex;
        align-items: center;
        gap: 12px;
        font-size: 14px;
      }
      .banner-text {
        flex: 1;
      }
      .impersonation-banner button {
        flex-shrink: 0;
      }
    `
  ]
})
export class ImpersonationBannerComponent implements OnInit, OnDestroy {
  protected readonly impersonation = inject(ImpersonationService);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly zone = inject(NgZone);
  private intervalId: ReturnType<typeof setInterval> | null = null;

  /**
   * Horloge réactive : sans signal qui change, le `computed` ci-dessous lisait
   * `Date.now()` une seule fois et le compte à rebours restait figé.
   */
  private readonly now = signal(Date.now());

  protected readonly remainingLabel = computed(() => {
    const ctx = this.impersonation.context();
    if (!ctx) return '';
    const remainingMs = ctx.expiresAt - this.now();
    if (remainingMs <= 0)
      return this.translate.instant('admin.impersonationBanner.expired');
    const m = Math.floor(remainingMs / 60_000);
    const s = Math.floor((remainingMs % 60_000) / 1000);
    return `${m}m${s.toString().padStart(2, '0')}s`;
  });

  ngOnInit(): void {
    // Tick chaque seconde, mais seulement pendant une impersonation : le
    // composant vit dans le shell global, donc pour tout le monde.
    // The token itself enforces 15 min server-side; l'expiration est testée à
    // chaque tick pour que l'UI reste honnête si l'admin s'est éloigné.
    // Hors zone : monté dans le shell global, un setInterval dans la zone
    // déclencherait une détection de changements de toute l'app chaque seconde.
    // Le signal suffit à rafraîchir ce seul composant.
    this.zone.runOutsideAngular(() => {
      this.intervalId = setInterval(() => {
        if (!this.impersonation.isActive()) return;
        this.now.set(Date.now());
        const expired = this.impersonation.checkExpiration();
        if (expired) {
          this.zone.run(() => void this.router.navigate(['/auth/login']));
        }
      }, 1_000);
    });
  }

  ngOnDestroy(): void {
    if (this.intervalId !== null) clearInterval(this.intervalId);
  }

  protected async exit(): Promise<void> {
    await this.impersonation.stop();
    void this.router.navigate(['/auth/login']);
  }
}
